import {
  Box,
  Button,
  Collapse,
  Icon,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { translate } from 'i18n';
import {
  FRENCH_SOURCES,
  BRAND_CITATIONS,
  DEEP_DIVE_REPORTS,
  type Source,
} from '../utils/roiSources';

// ---------------------------------------------------------------------------
// Methodology section IDs — used by result cards to deep-link
// ---------------------------------------------------------------------------
export const METHOD_IDS = {
  noshow: 'method-noshow',
  time: 'method-time',
  waitlist: 'method-waitlist',
  retention: 'method-retention',
  automation: 'method-automation',
  validation: 'method-validation',
} as const;

export type MethodId = typeof METHOD_IDS[keyof typeof METHOD_IDS];

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------
interface Citation {
  label: string;
  url: string;
}

interface MethodologyEntry {
  id: MethodId;
  title: string;
  body: string;
  citations?: Citation[];
}

const METHODOLOGY_ENTRIES: MethodologyEntry[] = [
  {
    id: METHOD_IDS.noshow,
    title: 'method_noshow_title',
    body: 'method_noshow_body',
    citations: [
      {
        label: 'foreUP — No-Show White Paper (10M+ rounds, 500+ courses)',
        url: 'https://cdn.prod.website-files.com/67f051f16e93cec5462d99e2/6822ac791af806fa7924ef0b_Whitepaper_NoShows_Q12025.pdf',
      },
      {
        label: 'Golf Consultants — Financial Impact of No-Shows (2025)',
        url: 'https://golf-consultants.com/2025/02/07/the-financial-impact-of-no-shows-on-your-golf-course-revenue/',
      },
    ],
  },
  {
    id: METHOD_IDS.time,
    title: 'method_recovery_title',
    body: 'method_recovery_body',
    citations: [
      {
        label: 'ManageSports — How to Increase Court Utilization by 40%',
        url: 'https://managesports.app/blog/increase-court-utilization-40-percent',
      },
    ],
  },
  {
    id: METHOD_IDS.waitlist,
    title: 'method_time_title',
    body: 'method_time_body',
    citations: [
      {
        label: 'LTA — Programming & Court Utilisation',
        url: 'https://www.lta.org.uk/roles-and-venues/venues/programming-court-utilisation/',
      },
      {
        label: 'ClubSpark — Coaching Waitlists',
        url: 'https://sportlabs.zendesk.com/hc/en-us/articles/360040579352-Coaching-Waiting-Lists',
      },
    ],
  },
  {
    id: METHOD_IDS.retention,
    title: 'method_retention_title',
    body: 'method_retention_body',
    citations: [
      {
        label: 'Bain & Company / Harvard Business School — Retention Economics',
        url: 'https://hbswk.hbs.edu/archive/the-economics-of-e-loyalty',
      },
      {
        label: 'ClassPass Look Back Report 2025 — Group Fitness Retention',
        url: 'https://classpass.com/blog/2025-classpass-look-back-report/',
      },
    ],
  },
  {
    id: METHOD_IDS.automation,
    title: 'method_automation_title',
    body: 'method_automation_body',
    citations: [
      {
        label: 'McKinsey Global Institute — Automatable Work',
        url: 'https://www.mckinsey.com/featured-insights/future-of-work',
      },
      {
        label: 'Formstack — Admin Time Survey',
        url: 'https://www.formstack.com/resources/report-workplace-productivity',
      },
    ],
  },
  {
    id: METHOD_IDS.validation,
    title: 'method_waitlist_title',
    body: 'method_waitlist_body',
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function MethodologySection({
  isOpen,
  onToggle,
  highlightId,
}: {
  isOpen: boolean;
  onToggle: () => void;
  highlightId: MethodId | null;
}) {
  return (
    <Box w="100%" mt={2} id="roi-methodology">
      <Button
        variant="ghost"
        size="sm"
        color="gray.500"
        fontWeight="500"
        onClick={onToggle}
        rightIcon={<Icon as={isOpen ? FaChevronUp : FaChevronDown} boxSize={3} />}
        _hover={{ color: 'gray.700' }}
        mx="auto"
        display="flex"
      >
        {translate('marketing.method_toggle')}
      </Button>
      <Collapse in={isOpen} animateOpacity>
        <Box
          mt={4}
          bg="gray.50"
          border="1px solid"
          borderColor="gray.100"
          borderRadius="xl"
          p={{ base: 5, md: 8 }}
        >
          <VStack spacing={5} align="stretch">
            {METHODOLOGY_ENTRIES.map((s) => (
              <Box
                key={s.id}
                id={s.id}
                bg={highlightId === s.id ? 'brand.50' : 'transparent'}
                border={highlightId === s.id ? '1px solid' : '1px solid transparent'}
                borderColor={highlightId === s.id ? 'brand.200' : 'transparent'}
                borderRadius="lg"
                p={3}
                mx={-3}
                transition="all 0.4s ease"
                scrollMarginTop="120px"
              >
                <Text fontWeight="600" fontSize="sm" color="gray.700" mb={1}>
                  {translate(`marketing.${s.title}` as any)}
                </Text>
                <Text fontSize="sm" color="gray.500" lineHeight="1.6">
                  {translate(`marketing.${s.body}` as any)}
                </Text>
                {s.citations && s.citations.length > 0 && (
                  <VStack align="flex-start" spacing={1} mt={2}>
                    {s.citations.map((c, i) => (
                      <Link
                        key={i}
                        href={c.url}
                        isExternal
                        fontSize="xs"
                        color="brand.500"
                        fontWeight="500"
                        _hover={{ color: 'brand.600', textDecoration: 'underline' }}
                      >
                        {c.label} ↗
                      </Link>
                    ))}
                  </VStack>
                )}
              </Box>
            ))}

            {/* ── 3-tier source system ── */}
            <Box pt={4} borderTop="1px solid" borderColor="gray.200">
              <Text fontWeight="700" fontSize="sm" color="gray.700" mb={3}>
                {translate('marketing.sources_title')}
              </Text>

              {/* Tier 1: French sources */}
              <Text fontWeight="600" fontSize="xs" color="gray.600" mb={1} mt={2}>
                {translate('marketing.sources_tier1')}
              </Text>
              <VStack align="flex-start" spacing={1} mb={3}>
                {FRENCH_SOURCES.map((s) => (
                  <Link
                    key={s.id}
                    href={s.url}
                    isExternal
                    fontSize="xs"
                    color="brand.500"
                    fontWeight="500"
                    _hover={{ color: 'brand.600', textDecoration: 'underline' }}
                  >
                    {s.source} — {s.label} ↗
                  </Link>
                ))}
              </VStack>

              {/* Tier 2: Brand citations */}
              <Text fontWeight="600" fontSize="xs" color="gray.600" mb={1} mt={2}>
                {translate('marketing.sources_tier2')}
              </Text>
              <VStack align="flex-start" spacing={1} mb={3}>
                {BRAND_CITATIONS.map((c) => (
                  <Text key={c.id} fontSize="xs" color="gray.500">
                    <Text as="span" fontWeight="500">{c.citation}</Text>
                    {' — '}{c.claim}
                  </Text>
                ))}
              </VStack>

              {/* Tier 3: Deep-dive reports */}
              <Text fontWeight="600" fontSize="xs" color="gray.600" mb={1} mt={2}>
                {translate('marketing.sources_tier3')}
              </Text>
              <VStack align="flex-start" spacing={1}>
                {DEEP_DIVE_REPORTS.map((r: Source) => (
                  <Link
                    key={r.id}
                    href={r.url}
                    isExternal
                    fontSize="xs"
                    color="brand.500"
                    fontWeight="500"
                    _hover={{ color: 'brand.600', textDecoration: 'underline' }}
                  >
                    {r.source} — {r.label}{r.isEnglish ? ' (en anglais)' : ''} ↗
                  </Link>
                ))}
              </VStack>
            </Box>

            <Box pt={2} borderTop="1px solid" borderColor="gray.200">
              <Text fontSize="xs" color="gray.400" lineHeight="1.6">
                {translate('marketing.method_disclaimer')}
              </Text>
            </Box>
          </VStack>
        </Box>
      </Collapse>
    </Box>
  );
}
